<template>
  <div>
    <PageHeader title="Onboardings" />

    <SearchToolbar
      class="mb-4"
      :placeholder="$t('Rechercher un magasin')"
      :available-filters="availableFilters"
      :selected-filters="selectedFilters"
      :initial-keywords="keywords"
      @update-filter-search="updateFilterSearch"
      @update-keyword-search="updateKeywords"
    />

    <Tabs :tabs="tabs" @tab-selected="selectTab" />

    <Table
      class="mt-4"
      :columns="columns"
      :data="getIndex"
      :is-accessible="true"
      :order="order"
      @view="redirectToStorePage"
      @sort="sortBy"
    >
      <template #progression="{ row }">
        <div class="flex flex-col">
          <span
            :class="[
              progressionClasses(row),
              'inline-block w-min items-center px-3 py-0.5 rounded-full text-xs font-medium',
            ]"
          >
            {{ $t(`store.onboarding.progression_status.${row.progression.status}`) }}
          </span>

          <span
            v-if="row.progression.ro"
            :class="[
              roClasses(row),
              'mt-1 inline-block w-min items-center px-3 py-0.5 rounded-full text-xs font-medium',
            ]"
          >
            {{ Math.round(row.progression.ro * 100) }} %
          </span>
        </div>
      </template>
      <template #statusPhase="{ row }">
        <div class="flex">
          <div class="rounded-lg">
            <h2 class="text-xs text-gray-700">
              {{ $t(`store_onboarding_progressions.status_phase.${row.statusPhase}`) }}
            </h2>
            <span
              v-if="row.actionRequired"
              class="bg-yellow-100 text-yellow-800 inline-block items-center px-3 py-0.5 mt-1 rounded-full text-xs font-medium"
            >
              Une action est requise
            </span>
          </div>
        </div>
      </template>
      <template #date="{ row }">
        <div class="flex">
          <div class="rounded-lg">
            <h2 class="text-xs text-gray-700">{{ row.date }}</h2>
            <span
              v-if="daysRemaining(row) > 0"
              class="bg-purple-100 text-purple-800 inline-block items-center px-3 py-0.5 mt-1 rounded-full text-xs font-medium"
            >
              {{ daysRemaining(row) }} jours restants
            </span>
            <span
              v-if="daysOfDelays(row) > 0"
              class="bg-red-100 text-red-800 inline-block items-center px-3 py-0.5 mt-1 rounded-full text-xs font-medium"
            >
              {{ daysOfDelays(row) }} jours de retard
            </span>
          </div>
        </div>
      </template>
      <template #salesChannel="{ row }">
        <div class="flex">
          <div class="rounded-lg">
            <h2 class="text-xs text-gray-700">{{ row.salesChannel }}</h2>
          </div>
        </div>
      </template>
    </Table>

    <Pagination
      :currentPage="currentPage"
      :totalPages="getTotalPages"
      :totalCount="getTotalCount"
      @changePage="handleChangePage"
    />
  </div>
</template>

<script>
import { Table, Tabs, Pagination, PageHeader, SearchToolbar, snakeCaseToWords, isPresent } from "@estaly/ui";
import { humanize } from "@/utils/utils";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    Pagination,
    PageHeader,
    SearchToolbar,
    Table,
    Tabs,
  },

  computed: {
    ...mapGetters("storeOnboardingProgressions", [
      "getIndex",
      "getTotalPages",
      "getTotalCount",
      "getAllowedStatusPhases",
      "getProgressions",
    ]),
    ...mapGetters("stores", ["getCategories"]),
    ...mapGetters("organizations", { organizationsIndex: "getIndex" }),

    columns() {
      if (!isPresent(this.getIndex)) return [];

      const slotKeys = ["date", "progression", "statusPhase", "salesChannel"];
      const percentageKeys = ["firstRo", "secondRo"];

      return Object.keys(this.getIndex[0])
        .filter((key) => key !== "actionRequired")
        .map((key) => {
          return {
            field: key,
            label: this.$t(`${humanize(key)}`),
            bold: false,
            slot: slotKeys.includes(key) ? key : null,
            percentage: percentageKeys.includes(key),
            sortable: key === "startAt",
          };
        });
    },
  },

  data() {
    return {
      currentStatus: null,
      tabs: [
        {
          name: "setup",
          current: true,
          displayName: "Setup",
        },
        {
          name: "formation",
          current: false,
          displayName: "Formation",
        },
        {
          name: "rampup",
          current: false,
          displayName: "Rampup",
        },
        {
          name: null,
          current: false,
          displayName: "Tous",
        },
      ],
      currentPage: 1,
      keywords: null,
      sortColumn: null,
      sortOrder: null,
      availableFilters: {
        category: [],
        statusPhase: [],
        progression: [],
        organization: [],
      },
      selectedFilters: {
        category: [],
        statusPhase: [],
        progression: [],
        organization: [],
      },
    };
  },

  async beforeMount() {
    this.currentStatus = this.tabs[0].name;
    await this.fetchOrganizations({ perPage: 300 });
    this.getStores();
  },

  mounted() {
    this.availableFilters.category = this.getCategories();
    this.availableFilters.statusPhase = this.getAllowedStatusPhases(this.currentStatus);
    this.availableFilters.progression = this.getProgressions();
    this.availableFilters.organization = this.organizationsIndex.map((organization) => organization.name);
  },

  methods: {
    ...mapActions("storeOnboardingProgressions", ["fetchIndex"]),
    ...mapActions("organizations", { fetchOrganizations: "fetchIndex" }),

    async getStores() {
      if (this.sortColumn) {
        const direction = this.sortOrder === "ASC" ? "" : "-";
        this.order = `${direction}${this.sortColumn}`;
      }

      await this.fetchIndex({
        status: this.currentStatus,
        keywords: this.keywords,
        category: this.selectedFilters.category,
        statusPhase: this.selectedFilters.statusPhase,
        progression: this.selectedFilters.progression,
        organization: this.selectedFilters.organization,
        page: this.currentPage,
        order: snakeCaseToWords(this.order),
      });
    },

    redirectToStorePage(row) {
      window.open(`/stores/${row.id}`, "_blank");
    },

    selectTab(tab) {
      this.currentStatus = tab?.name;
      this.selectedFilters.statusPhase = [];
      this.handleChangePage(1);
    },

    updateFilterSearch(filterName, filterOptions) {
      this.selectedFilters[filterName] = filterOptions;
      this.handleChangePage(1);
    },

    updateKeywords(keywords) {
      this.keywords = keywords;
      this.handleChangePage(1);
    },

    handleChangePage(newPage) {
      this.currentPage = newPage;
      this.getStores();
    },

    progressionClasses(row) {
      if (row.progression.status === "late") return "bg-red-100 text-red-800";
      if (row.progression.status === "ahead_of_schedule") return "bg-green-100 text-green-800";
      return "bg-blue-100 text-blue-800";
    },

    roClasses(row) {
      if (row.progression.status === "late") return "text-red-800";
      if (row.progression.status === "ahead_of_schedule") return "text-green-800";
      return "text-blue-800";
    },

    daysOfDelays(row) {
      if (!row.progression) return 0;
      return row.progression.daysRemaining < 0 ? Math.abs(row.progression.daysRemaining) : 0;
    },

    daysRemaining(row) {
      if (!row.progression) return 0;
      return row.progression.daysRemaining > 0 ? row.progression.daysRemaining : 0;
    },

    async sortBy(column) {
      if (!isPresent(this.getIndex)) return;

      this.sortColumn = column.field;
      this.sortOrder = this.sortOrder === "ASC" ? "DESC" : "ASC";
      this.handleChangePage(1);
    },
  },
};
</script>
